import { useState, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useHelp } from './HelpContext'
import './HelpPanel.css'

interface HelpPanelProps {
  sectionFile: string
  isOpen: boolean
  onClose: () => void
}

const MAIN_SECTIONS = new Set([
  'strategy-workspace', 'business-graph', 'oracle-insights', 'execution',
  'review', 'canvas', 'document', 'vigil', 'dossier', 'agency', 'settings',
])

function truncateToWhatSection(markdown: string): string {
  const marker = '## When to use it'
  const idx = markdown.indexOf(marker)
  if (idx === -1) return markdown
  return markdown.substring(0, idx).trimEnd()
}

export default function HelpPanel({ sectionFile, isOpen, onClose }: HelpPanelProps) {
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !sectionFile) return
    setLoading(true)
    fetch(`/docs/user-guide/${sectionFile}.md`)
      .then(res => res.ok ? res.text() : '')
      .then(text => {
        setContent(truncateToWhatSection(text))
        setLoading(false)
      })
      .catch(() => {
        setContent('Help content not available.')
        setLoading(false)
      })
  }, [isOpen, sectionFile])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  return (
    <>
      <div className="help-panel-backdrop" onClick={onClose} />
      <div className="help-panel">
        <div className="help-panel-header">
          <span className="help-panel-title">Help</span>
          <button className="help-panel-close" onClick={onClose}>&times;</button>
        </div>
        <div className="help-panel-body">
          {loading ? (
            <p className="help-panel-loading">Loading...</p>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          )}
        </div>
        {MAIN_SECTIONS.has(sectionFile) && (
          <div className="help-panel-footer">
            <a
              href={`/help#${sectionFile}`}
              target="_blank"
              rel="noopener noreferrer"
              className="help-panel-readmore"
            >
              Read full guide &rarr;
            </a>
          </div>
        )}
      </div>
    </>
  )
}

interface HelpTriggerProps {
  section: string
}

export function HelpTrigger({ section }: HelpTriggerProps) {
  const { helpSection, setHelpSection } = useHelp()
  const isActive = helpSection === section
  return (
    <button
      className={`help-trigger ${isActive ? 'active' : ''}`}
      onClick={(e) => {
        e.stopPropagation()
        setHelpSection(isActive ? null : section)
      }}
      title="Help"
      aria-label={`Help for ${section}`}
    >
      ?
    </button>
  )
}

export function GlobalHelpPanel() {
  const { helpSection, setHelpSection } = useHelp()
  return (
    <HelpPanel
      sectionFile={helpSection || ''}
      isOpen={helpSection !== null}
      onClose={() => setHelpSection(null)}
    />
  )
}
